import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
	type CSSProperties,
	type ReactNode,
} from "react";
import {
	KEY_STANDS,
	MINUTE,
	type Billet,
	type EventKind,
	type Interval,
	type RollChange,
	type Selection,
	type TimeRange,
	type TimelineData,
	type TimelineView,
} from "../models";
import {
	billetObservedEnd,
	dateTimeText,
	durationText,
	numberText,
	packIntervals,
	timeText,
} from "../timeline";

const PIXELS_PER_MINUTE = 80;
const SCALE = PIXELS_PER_MINUTE / MINUTE;
const HEADER_HEIGHT = 46;
export interface TimelineViewportHandle {
	scrollToTime: (time: number) => void;
}
interface Props {
	data: TimelineData;
	range: TimeRange;
	view: TimelineView;
	selection: Selection | null;
	onSelect: (selection: Selection) => void;
	onPosition: (time: number, manual: boolean) => void;
	initialTime: number;
}
interface TimelineMark extends Interval {
	billet: Billet;
	unmeasured: boolean;
}
interface RollGroup extends Interval {
	members: RollChange[];
}

function groupRolls(rolls: RollChange[]): RollGroup[] {
	const groups: RollGroup[] = [];
	for (const roll of rolls) {
		const last = groups[groups.length - 1];
		if (
			last &&
			(roll.start - last.start) * SCALE < last.members.length * 48 + 6
		)
			last.members.push(roll);
		else
			groups.push({
				Id: roll.Id,
				start: roll.start,
				end: roll.start,
				members: [roll],
			});
	}
	return groups;
}

export const TimelineViewport = forwardRef<TimelineViewportHandle, Props>(
	function TimelineViewport(
		{ data, range, view, selection, onSelect, onPosition, initialTime },
		ref,
	) {
		const scrollRef = useRef<HTMLDivElement>(null);
		const programmaticTop = useRef<number | null>(null);
		const previousTop = useRef(0);
		const frame = useRef<number | null>(null);
		const [viewport, setViewport] = useState({
			top: Math.max(0, (initialTime - range.start) * SCALE),
			height: 700,
		});
		const height = Math.max(300, (range.end - range.start) * SCALE + 100);
		const minY = Math.max(0, viewport.top - 250);
		const maxY = viewport.top + viewport.height + 250;
		const selected = (kind: EventKind, id: number) =>
			selection?.kind === kind && selection.id === id;

		const scrollToTime = (time: number) => {
			const element = scrollRef.current;
			if (!element) return;
			const target = Math.max(
				0,
				Math.min(
					(time - range.start) * SCALE,
					element.scrollHeight - element.clientHeight,
				),
			);
			programmaticTop.current = target;
			previousTop.current = target;
			element.scrollTop = target;
			setViewport({ top: target, height: element.clientHeight });
			onPosition(range.start + target / SCALE, false);
		};
		useImperativeHandle(ref, () => ({ scrollToTime }));
		useEffect(() => {
			const element = scrollRef.current;
			if (!element) return;
			const observer = new ResizeObserver(() =>
				setViewport((v) => ({ ...v, height: element.clientHeight })),
			);
			observer.observe(element);
			scrollToTime(initialTime);
			return () => {
				observer.disconnect();
				if (frame.current !== null) cancelAnimationFrame(frame.current);
			};
			// The viewport remounts for a new applied range. View changes retain its scroll.
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, []);

		const marks = useMemo(() => {
			const columns = new Map<
				string,
				ReturnType<typeof packIntervals<TimelineMark>>
			>();
			const parallel: TimelineMark[] = data.billets.map((b) => ({
				Id: b.Id,
				start: b.start,
				end: billetObservedEnd(b),
				billet: b,
				unmeasured: b.end === null || b.end <= b.start,
			}));
			columns.set(
				"parallel",
				packIntervals(parallel, range, PIXELS_PER_MINUTE),
			);
			for (const stand of KEY_STANDS) {
				const intervals = data.billets.flatMap((b) =>
					b.passages
						.filter((p) => p.StandId === stand && p.end >= p.start)
						.map((p) => ({
							Id: b.Id,
							start: p.start,
							end: p.end,
							billet: b,
							unmeasured: p.end === p.start,
						})),
				);
				columns.set(
					String(stand),
					packIntervals(intervals, range, PIXELS_PER_MINUTE),
				);
			}
			return columns;
		}, [data.billets, range]);
		const rollGroups = useMemo(() => groupRolls(data.rolls), [data.rolls]);
		const stops = useMemo(
			() =>
				packIntervals(
					data.stops.map((s) => ({
						...s,
						recordedEnd: s.end,
						end: s.end ?? Math.min(Date.now(), range.end),
					})),
					range,
					PIXELS_PER_MINUTE,
					42,
				),
			[data.stops, range],
		);
		const rhf = useMemo(
			() =>
				packIntervals(
					data.billets.map((b) => ({ ...b, end: b.start })),
					range,
					PIXELS_PER_MINUTE,
					24,
				),
			[data.billets, range],
		);
		const columns =
			view === "parallel"
				? [
						"Campaign",
						"Work order",
						"RHF",
						"Billets in line",
						"Stops",
						"Roll changes",
					]
				: [
						"Campaign",
						"Work order",
						"RHF",
						"H1",
						"H9",
						"H15",
						"H17",
						"Stops",
						"Roll changes",
					];
		const kindClasses = `timeline-grid timeline-grid-${view}`;
		const columnStyle = (column: number): CSSProperties => ({
			gridColumn: column + 2,
		});
		const isVisible = (top: number, size: number) =>
			top <= maxY && top + size >= minY;
		const labelOffset = (top: number, size: number, labelHeight: number) =>
			Math.max(0, Math.min(viewport.top - top, size - labelHeight));

		const band = (
			item: Interval,
			kind: "campaign" | "workOrder",
			title: string,
			info: ReactNode,
		) => {
			const top = Math.max(0, (item.start - range.start) * SCALE);
			const bottom =
				(Math.min(item.end ?? range.end, range.end) - range.start) * SCALE;
			const size = Math.max(2, bottom - top);
			if (!isVisible(top, size)) return null;
			return (
				<button
					key={item.Id}
					type="button"
					className={`timeline-event timeline-band timeline-${kind}${selected(kind, item.Id) ? " is-selected" : ""}`}
					style={{ top, height: size }}
					aria-label={`${title}, ${dateTimeText(item.start)}`}
					aria-pressed={selected(kind, item.Id)}
					onClick={() => onSelect({ kind, id: item.Id })}
				>
					<span
						className="timeline-band-label"
						style={{ transform: `translateY(${labelOffset(top, size, 84)}px)` }}
					>
						<strong>{title}</strong>
						{info}
						<small>{timeText(item.start)}</small>
					</span>
				</button>
			);
		};

		const billetColumn = (key: string) =>
			(marks.get(key) ?? [])
				.filter((m) => isVisible(m.top, m.height))
				.map((m) => {
					const b = m.item.billet;
					const isSelected = selected("billet", b.Id);
					const title = `Billet ${b.Id} · ${dateTimeText(m.item.start)} · ${m.item.unmeasured ? "Duration not measured" : durationText(m.item.start, m.item.end)} · Entry ${numberText(b.StartTemperatureAvg)} / Exit ${numberText(b.EndTemperatureAvg)}`;
					return (
						<button
							type="button"
							key={b.Id}
							className={`timeline-event timeline-billet${b.IsGood === false ? " timeline-cobble" : ""}${m.item.unmeasured ? " is-unmeasured" : ""}${b.IsEstimated ? " is-estimated" : ""}${isSelected ? " is-selected" : ""}`}
							style={{
								top: m.top,
								height: m.height,
								left: `calc(${(m.lane / m.lanes) * 100}% + 3px)`,
								width: `calc(${100 / m.lanes}% - 6px)`,
							}}
							title={title}
							aria-label={title}
							aria-pressed={isSelected}
							onClick={() => onSelect({ kind: "billet", id: b.Id })}
						>
							<strong>{b.Id}</strong>
							{m.height >= 55 && (
								<small>
									{m.item.unmeasured
										? "End not recorded"
										: durationText(m.item.start, m.item.end)}
								</small>
							)}
							{m.height >= 78 && (
								<small
									title={`Entry range ${numberText(b.StartTemperatureMin)} – ${numberText(b.StartTemperatureMax)}; exit range ${numberText(b.EndTemperatureMin)} – ${numberText(b.EndTemperatureMax)}`}
								>
									Temp{" "}
									{numberText(b.EndTemperatureAvg ?? b.StartTemperatureAvg)}{" "}
									<span className="timeline-temperature-source">
										{b.EndTemperatureAvg !== null ? "exit" : "entry"}
									</span>
								</small>
							)}
							{m.height >= 102 && <small>WO {b.WorkOrder ?? "—"}</small>}
						</button>
					);
				});

		const ticks: ReactNode[] = [];
		const firstMinute = Math.floor(minY / PIXELS_PER_MINUTE);
		const lastMinute = Math.ceil(Math.min(height, maxY) / PIXELS_PER_MINUTE);
		for (let minute = firstMinute; minute <= lastMinute; minute++) {
			const time = range.start + minute * MINUTE;
			if (time >= range.end) break;
			ticks.push(
				<div
					className="timeline-tick"
					key={minute}
					style={{ top: minute * PIXELS_PER_MINUTE }}
				>
					<span>
						{new Date(time).toLocaleTimeString([], {
							hour: "2-digit",
							minute: "2-digit",
							hour12: false,
						})}
					</span>
				</div>,
			);
		}
		const midnight = new Date(range.start + minY / SCALE);
		midnight.setHours(0, 0, 0, 0);
		const days: ReactNode[] = [];
		while (
			midnight.getTime() < range.end &&
			(midnight.getTime() - range.start) * SCALE <= maxY
		) {
			const top = (midnight.getTime() - range.start) * SCALE;
			if (top >= minY)
				days.push(
					<div
						key={midnight.getTime()}
						className="timeline-day"
						style={{ top }}
					>
						{midnight.toLocaleDateString()}
					</div>,
				);
			midnight.setDate(midnight.getDate() + 1);
		}

		return (
			<div
				ref={scrollRef}
				className="timeline-scroll"
				tabIndex={0}
				aria-label="Mill operations timeline"
				onWheel={() => {
					programmaticTop.current = null;
				}}
				onTouchStart={() => {
					programmaticTop.current = null;
				}}
				onKeyDown={() => {
					programmaticTop.current = null;
				}}
				onScroll={() => {
					if (frame.current !== null) cancelAnimationFrame(frame.current);
					frame.current = requestAnimationFrame(() => {
						const element = scrollRef.current;
						if (!element) return;
						const top = element.scrollTop;
						const movedVertically = Math.abs(previousTop.current - top) > 1;
						const manual =
							movedVertically &&
							(programmaticTop.current === null ||
								Math.abs(programmaticTop.current - top) > 1);
						previousTop.current = top;
						programmaticTop.current = null;
						setViewport({ top, height: element.clientHeight });
						onPosition(range.start + top / SCALE, manual);
					});
				}}
			>
				<div
					className={`${kindClasses} timeline-head`}
					style={{ height: HEADER_HEIGHT }}
				>
					<div>
						Local ↓
						<small className="timeline-header-date">
							{new Date(range.start + viewport.top / SCALE).toLocaleDateString(
								[],
								{ month: "short", day: "numeric" },
							)}
						</small>
					</div>
					{columns.map((name) => (
						<div key={name}>{name}</div>
					))}
				</div>
				<div className={`${kindClasses} timeline-body`} style={{ height }}>
					{ticks}
					{days}
					<div className="timeline-column" style={columnStyle(0)}>
						{data.campaigns.map((c) =>
							band(
								{ ...c, end: c.end ?? c.nextStart ?? null },
								"campaign",
								String(c.Id),
								<>
									<small>{c.SectionCode}</small>
									<small>{c.Product}</small>
								</>,
							),
						)}
					</div>
					<div className="timeline-column" style={columnStyle(1)}>
						{data.workOrders.map((w) =>
							band(
								w,
								"workOrder",
								`WO ${w.WorkOrder ?? "Unassigned"}`,
								<>
									<small>{w.SectionCode}</small>
									<small>{w.Product}</small>
								</>,
							),
						)}
					</div>
					<div className="timeline-column" style={columnStyle(2)}>
						{rhf
							.filter((m) => isVisible(m.top, m.height))
							.map((m) => (
								<button
									type="button"
									key={m.item.Id}
									className={`timeline-rhf${selected("billet", m.item.Id) ? " is-selected" : ""}`}
									style={{
										top: m.top,
										left: `${(m.lane / m.lanes) * 100}%`,
										width: `${100 / m.lanes}%`,
									}}
									title={`RHF discharge ${dateTimeText(m.item.start)}`}
									aria-label={`Select billet ${m.item.Id} at RHF`}
									aria-pressed={selected("billet", m.item.Id)}
									onClick={() => onSelect({ kind: "billet", id: m.item.Id })}
								>
									◆ {m.item.Id}
								</button>
							))}
					</div>
					{view === "parallel" ? (
						<div className="timeline-column" style={columnStyle(3)}>
							{billetColumn("parallel")}
						</div>
					) : (
						KEY_STANDS.map((s, i) => (
							<div
								className="timeline-column"
								key={s}
								style={columnStyle(i + 3)}
							>
								{billetColumn(String(s))}
							</div>
						))
					)}
					<div
						className="timeline-column"
						style={columnStyle(columns.length - 2)}
					>
						{stops
							.filter((m) => isVisible(m.top, m.height))
							.map((m) => (
								<button
									type="button"
									key={m.item.Id}
									className={`timeline-event timeline-stop${selected("stop", m.item.Id) ? " is-selected" : ""}`}
									style={{
										top: m.top,
										height: m.height,
										left: `calc(${(m.lane / m.lanes) * 100}% + 3px)`,
										width: `calc(${100 / m.lanes}% - 6px)`,
									}}
									title={`${m.item.Cause || "Not declared"} · ${dateTimeText(m.item.start)} – ${m.item.recordedEnd === null ? "Open" : dateTimeText(m.item.recordedEnd)}`}
									aria-pressed={selected("stop", m.item.Id)}
									onClick={() => onSelect({ kind: "stop", id: m.item.Id })}
								>
									<span
										className="timeline-band-label"
										style={{
											transform: `translateY(${labelOffset(m.top, m.height, 58)}px)`,
										}}
									>
										<strong>{m.item.Cause || "Not declared"}</strong>
										{m.height >= 56 && (
											<small>
												{m.item.recordedEnd === null
													? "Open"
													: durationText(m.item.start, m.item.recordedEnd)}
											</small>
										)}
									</span>
								</button>
							))}
					</div>
					<div
						className="timeline-column"
						style={columnStyle(columns.length - 1)}
					>
						{rollGroups
							.filter((g) =>
								isVisible(
									(g.start - range.start) * SCALE,
									g.members.length * 48,
								),
							)
							.map((g) => (
								<div
									key={g.Id}
									className="timeline-roll-group"
									style={{ top: (g.start - range.start) * SCALE }}
								>
									{g.members.map((r) => (
										<button
											type="button"
											key={r.Id}
											className={`timeline-roll-row${selected("roll", r.Id) ? " is-selected" : ""}`}
											aria-pressed={selected("roll", r.Id)}
											title={`${dateTimeText(r.start)} · ${r.Stand} · ${r.PreviousRollset ?? "—"} → ${r.InstalledRollset ?? "Removed"}`}
											onClick={() => onSelect({ kind: "roll", id: r.Id })}
										>
											<strong>
												{r.Stand} ·{" "}
												{r.InstalledRollset ??
													`Removed ${r.PreviousRollset ?? "rollset"}`}
											</strong>
											<small>{timeText(r.start)}</small>
										</button>
									))}
								</div>
							))}
					</div>
					{[
						...data.campaigns.map((c) => ({
							...c,
							kind: "campaign" as const,
							text: `Campaign ${c.Id}`,
						})),
						...data.workOrders.map((w) => ({
							...w,
							kind: "workOrder" as const,
							text: `WO ${w.WorkOrder ?? "Unassigned"} · first billet ${w.FirstBilletId}`,
						})),
					]
						.filter(
							(e) =>
								e.start >= range.start &&
								e.start < range.end &&
								isVisible((e.start - range.start) * SCALE, 20),
						)
						.map((e) => (
							<button
								type="button"
								key={`${e.kind}-${e.Id}`}
								className={`timeline-boundary timeline-${e.kind}${selected(e.kind, e.Id) ? " is-selected" : ""}`}
								style={{ top: (e.start - range.start) * SCALE }}
								aria-label={`${e.text}, ${dateTimeText(e.start)}`}
								aria-pressed={selected(e.kind, e.Id)}
								onClick={() => onSelect({ kind: e.kind, id: e.Id })}
							>
								<span>{e.text}</span>
							</button>
						))}
				</div>
			</div>
		);
	},
);
